// src/pages/admin/StaffDetailPage.js

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStaff } from "../../contexts/StaffContext";
import { useAuth } from "../../contexts/AuthContext";
import { useVacation } from "../../contexts/VacationContext";
import TimeManagementTab from "../../components/staff/TimeManagementTab";
import VacationAccountCard from "../../components/staff/VacationAccountCard";
import VacationAccountModal from "../../components/staff/VacationAccountModal";
import HoursAccountCard from "../../components/staff/HoursAccountCard";
import HoursAccountModal from "../../components/staff/HoursAccountModal";
import { ScheduleException } from "../../models/StaffModels";
import "../../styles/staff-core.css";
import "../../styles/staff-scheduling.css";
import "../../styles/staff-forms-modals.css";

const StaffDetailPage = () => {
  const { t } = useTranslation(["staff", "common"]);
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { currentCompany } = useAuth();
  const companyId = currentCompany?.id;
  const {
    fetchStaffMember,
    updateStaffMember,
    deleteStaffMember,
    archiveStaffMember,       // ARCHIVE
    reactivateStaffMember,    // ARCHIVE
    isPinTakenInDepartments,  // ARCHIVE — used by handleSaveInfo's dup-check (§3b)
    fetchScheduleExceptions,
    addScheduleException,
    deleteScheduleException,
    departments,
    getDepartmentName,
  } = useStaff();

  const { fetchVacationsByStaff } = useVacation();

  const [staff, setStaff] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scheduleExceptions, setScheduleExceptions] = useState([]);

  const [staffVacations, setStaffVacations] = useState([]);
  const [vacationsLoading, setVacationsLoading] = useState(false);

  const [editModeInfo, setEditModeInfo] = useState(false);
  const [editModeDepartments, setEditModeDepartments] = useState(false);
  const [editModeExceptions, setEditModeExceptions] = useState(false);

  // ---------------------------------------------------------------------------
  // Vacation modal state
  // ---------------------------------------------------------------------------

  const [isVacationModalOpen, setIsVacationModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString(),
  );

  // ---------------------------------------------------------------------------
  // Hours account modal state
  // ---------------------------------------------------------------------------

  const [isHoursModalOpen, setIsHoursModalOpen] = useState(false);
  const [selectedHoursYear, setSelectedHoursYear] = useState(
    new Date().getFullYear().toString(),
  );
  const [hoursAccount, setHoursAccount] = useState(null);
  const [hoursRefreshTrigger, setHoursRefreshTrigger] = useState(0);

  // ---------------------------------------------------------------------------
  // Form state
  // ---------------------------------------------------------------------------

  const [formDataInfo, setFormDataInfo] = useState({
    name: "",
    email: "",
    pin: "",
    role: "",
  });

  const [formDataDepartments, setFormDataDepartments] = useState({
    primaryDepartmentId: "",
    departmentIds: [],
  });

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const hasPinSet = (staffData) => {
    return (staffData.pinHash && staffData.pinSalt) || staffData.pin;
  };

  // ---------------------------------------------------------------------------
  // Vacation modal handlers
  // ---------------------------------------------------------------------------

  const handleOpenVacationModal = (year) => {
    console.log("Opening vacation modal for year:", year);
    console.log("Staff data:", { staffId, companyId, staffName: staff?.name });
    setSelectedYear(year || new Date().getFullYear().toString());
    setIsVacationModalOpen(true);
  };

  const handleCloseVacationModal = () => {
    console.log("Closing vacation modal");
    setIsVacationModalOpen(false);
  };

  // ---------------------------------------------------------------------------
  // Hours account modal handlers
  // ---------------------------------------------------------------------------

  const handleOpenHoursModal = (year) => {
    setSelectedHoursYear(year || new Date().getFullYear().toString());
    setIsHoursModalOpen(true);
  };

  const handleCloseHoursModal = (didChange) => {
    setIsHoursModalOpen(false);
    if (didChange) {
      setHoursRefreshTrigger((prev) => prev + 1);
    }
  };

  const handleHoursAccountLoaded = (account) => {
    setHoursAccount(account);
  };

  /**
   * Called by HoursAccountModal immediately after a successful adjustment
   * add/delete (independent of the modal closing). The service call already
   * returns the fully updated account, so we push it straight into state and
   * bump hoursRefreshTrigger so HoursAccountCard re-fetches in sync — no
   * extra Firestore read needed, and no page reload required.
   */
  const handleHoursAccountChanged = (updatedAccount) => {
    setHoursAccount(updatedAccount);
    setHoursRefreshTrigger((prev) => prev + 1);
  };

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const loadStaffData = async () => {
      if (!staffId || !companyId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const staffData = await fetchStaffMember(staffId);
        setStaff(staffData);

        setFormDataInfo({
          name: staffData.name,
          email: staffData.email || "",
          pin: "",
          role: staffData.role || "staff",
        });

        setFormDataDepartments({
          primaryDepartmentId: staffData.primaryDepartmentId || "",
          departmentIds: staffData.departmentIds || [],
        });

        const exceptions = await fetchScheduleExceptions(staffId);
        setScheduleExceptions(exceptions);

        setError(null);
      } catch (err) {
        console.error("Error loading staff data:", err);
        setError(t("detail.failedToLoad"));
      } finally {
        setLoading(false);
      }
    };

    loadStaffData();
  }, [staffId, companyId, fetchStaffMember, fetchScheduleExceptions, t]);

  /**
   * Fetch vacation bookings from the vacations collection for this staff member.
   * These are vacation bookings made via the Team Vacation Calendar and are stored
   * separately from schedule exceptions.
   */
  useEffect(() => {
    const loadStaffVacations = async () => {
      if (!staffId || !companyId) return;

      setVacationsLoading(true);
      try {
        const currentYear = new Date().getFullYear();
        const startDate = new Date(currentYear, 0, 1);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(currentYear, 11, 31);
        endDate.setHours(23, 59, 59, 999);

        const result = await fetchVacationsByStaff(staffId, startDate, endDate);
        setStaffVacations(result || []);
      } catch (err) {
        console.error("Error loading staff vacations:", err);
        setStaffVacations([]);
      } finally {
        setVacationsLoading(false);
      }
    };

    loadStaffVacations();
  }, [staffId, companyId, fetchVacationsByStaff]);

  /**
   * Safely extract a "YYYY-MM-DD" string from a vacation object.
   * Handles both class instances with getDateString() and plain Firestore objects.
   */
  const getVacationDateString = (vacation) => {
    if (typeof vacation.getDateString === "function") {
      return vacation.getDateString();
    }
    if (vacation.date?.toDate) {
      return vacation.date.toDate().toISOString().split("T")[0];
    }
    if (vacation.date instanceof Date) {
      return vacation.date.toISOString().split("T")[0];
    }
    return "";
  };

  // ---------------------------------------------------------------------------
  // Tab edit-mode helpers
  // ---------------------------------------------------------------------------

  const getCurrentEditMode = () => {
    switch (activeTab) {
      case "info":
        return editModeInfo;
      case "departments":
        return editModeDepartments;
      case "exceptions":
        return editModeExceptions;
      case "timeManagement":
        return false;
      default:
        return false;
    }
  };

  const handleEditClick = () => {
    switch (activeTab) {
      case "info":
        setEditModeInfo(true);
        break;
      case "departments":
        setEditModeDepartments(true);
        break;
      case "exceptions":
        setEditModeExceptions(true);
        break;
      case "timeManagement":
        break;
    }
  };

  const handleCancel = () => {
    switch (activeTab) {
      case "info":
        setEditModeInfo(false);
        setFormDataInfo({
          name: staff.name,
          email: staff.email || "",
          pin: "",
          role: staff.role || "staff",
        });
        break;
      case "departments":
        setEditModeDepartments(false);
        setFormDataDepartments({
          primaryDepartmentId: staff.primaryDepartmentId || "",
          departmentIds: staff.departmentIds || [],
        });
        break;
      case "exceptions":
        setEditModeExceptions(false);
        break;
      case "timeManagement":
        break;
    }
    setError(null);
  };

  // ---------------------------------------------------------------------------
  // Form input handlers
  // ---------------------------------------------------------------------------

  const handleInfoInputChange = (e) => {
    const { name, value } = e.target;
    setFormDataInfo({
      ...formDataInfo,
      [name]: value,
    });
  };

  const handleDepartmentChange = (e) => {
    const { options } = e.target;
    const departmentIds = Array.from(options)
      .filter((option) => option.selected)
      .map((option) => option.value);

    setFormDataDepartments({
      ...formDataDepartments,
      departmentIds,
    });
  };

  const handlePrimaryDepartmentChange = (e) => {
    setFormDataDepartments({
      ...formDataDepartments,
      primaryDepartmentId: e.target.value,
    });
  };

  // ---------------------------------------------------------------------------
  // Save handlers
  // ---------------------------------------------------------------------------

  const handleSaveInfo = async () => {
    try {
      setLoading(true);

      // ARCHIVE (§3b): block save if the new PIN collides with an active
      // colleague in any shared department. updateStaffMember does NOT run
      // this check internally (confirmed against StaffContext.js) — it
      // hashes whatever PIN it's given. Archived colleagues never trigger
      // a conflict (isPinTakenInDepartments skips active === false docs).
      if (formDataInfo.pin.trim()) {
        const conflict = await isPinTakenInDepartments(
          formDataInfo.pin,
          staff.departmentIds || [],
          staffId
        );
        if (conflict) {
          setError(
            t("validation.pinTakenInDepartment", { name: conflict.name })
          );
          setLoading(false);
          return;
        }
      }

      const updatedStaff = {
        name: formDataInfo.name,
        email: formDataInfo.email,
        role: formDataInfo.role,
      };

      if (formDataInfo.pin.trim()) {
        updatedStaff.pin = formDataInfo.pin;
      }

      await updateStaffMember(staffId, updatedStaff);

      setStaff({
        ...staff,
        ...updatedStaff,
      });

      setEditModeInfo(false);
      setFormDataInfo({
        ...formDataInfo,
        pin: "",
      });
      setError(null);
    } catch (err) {
      console.error("Error saving staff information:", err);
      setError(t("errors.failedToSaveInfo"));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDepartments = async () => {
    try {
      setLoading(true);

      let departmentIds = [...formDataDepartments.departmentIds];
      if (
        formDataDepartments.primaryDepartmentId &&
        !departmentIds.includes(formDataDepartments.primaryDepartmentId)
      ) {
        departmentIds.push(formDataDepartments.primaryDepartmentId);
      }

      const updatedStaff = {
        primaryDepartmentId: formDataDepartments.primaryDepartmentId,
        departmentIds,
      };

      await updateStaffMember(staffId, updatedStaff);

      setStaff({
        ...staff,
        ...updatedStaff,
      });

      setEditModeDepartments(false);
      setError(null);
    } catch (err) {
      console.error("Error saving departments:", err);
      setError(t("errors.failedToSaveDepartments"));
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Exception handlers
  // ---------------------------------------------------------------------------

  const handleAddException = async (exceptionData) => {
    try {
      const newException = new ScheduleException(
        null,
        new Date(exceptionData.date),
        exceptionData.type,
        parseFloat(exceptionData.hours),
        exceptionData.description,
      );

      await addScheduleException(staffId, newException);

      setScheduleExceptions([...scheduleExceptions, newException]);

      setError(null);
    } catch (err) {
      console.error("Error adding schedule exception:", err);
      setError(t("errors.failedToAddException"));
    }
  };

  const handleDeleteException = async (exceptionId) => {
    try {
      await deleteScheduleException(staffId, exceptionId);

      setScheduleExceptions(
        scheduleExceptions.filter((ex) => ex.id !== exceptionId),
      );

      setError(null);
    } catch (err) {
      console.error("Error deleting schedule exception:", err);
      setError(t("errors.failedToDeleteException"));
    }
  };

  // ---------------------------------------------------------------------------
  // Staff archive / reactivate / permanent delete / navigation
  // ARCHIVE (§3a): "delete" is now "archive" for active staff; permanent
  // delete is only reachable once a staff member is already archived.
  // ---------------------------------------------------------------------------

  const handleArchiveStaff = async () => {
    if (!window.confirm(t("archive.confirmBody"))) return;

    try {
      await archiveStaffMember(staffId);
      navigate("/admin/staff");
    } catch (err) {
      console.error("Error archiving staff member:", err);
      setError(
        err.message === "CANNOT_ARCHIVE_WHILE_CLOCKED_IN"
          ? t("archive.cannotArchiveWhileClockedIn")
          : err.message
      );
    }
  };

  const handleReactivateStaff = async () => {
    const newPin = window.prompt(t("archive.reactivateNeedsNewPin"));
    if (!newPin) return;

    try {
      // reactivateStaffMember already re-runs the duplicate-PIN check
      // internally (isPinTakenInDepartments) and throws Error('PIN_TAKEN')
      // with a .conflict = { id, name } property when it finds one —
      // no separate client-side pre-check needed here.
      await reactivateStaffMember(staffId, newPin);
      setStaff((prev) => ({ ...prev, active: true }));
      setError(null);
    } catch (err) {
      console.error("Error reactivating staff member:", err);
      if (err.message === "PIN_TAKEN" && err.conflict) {
        setError(
          t("validation.pinTakenInDepartment", { name: err.conflict.name })
        );
      } else {
        setError(err.message);
      }
    }
  };

  const handleDeleteStaff = async () => {
    if (window.confirm(t("archive.confirmDeletePermanently"))) {
      try {
        await deleteStaffMember(staffId);
        navigate("/admin/staff");
      } catch (err) {
        console.error("Error deleting staff member:", err);
        setError(t("errors.failedToDeleteStaff"));
      }
    }
  };

  const handleBack = () => {
    navigate("/admin/staff");
  };

  // ---------------------------------------------------------------------------
  // Guard renders
  // ---------------------------------------------------------------------------

  if (!companyId || (loading && !staff)) {
    return (
      <div className="loading-indicator">{t("detail.loadingDetails")}</div>
    );
  }

  if (error && !staff) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={handleBack} className="back-button">
          {t("detail.backToList")}
        </button>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="error-container">
        <div className="error-message">{t("detail.notFound")}</div>
        <button onClick={handleBack} className="back-button">
          {t("detail.backToList")}
        </button>
      </div>
    );
  }

  const currentEditMode = getCurrentEditMode();
  const isArchived = staff.active === false; // ARCHIVE

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className="staff-detail-container">
      <div className="staff-detail-header">
        <button onClick={handleBack} className="back-button">
          &larr; {t("detail.backToList")}
        </button>
        <h1>
          {staff.name}
          {isArchived && (
            <span
              className="badge badge-archived"
              style={{
                marginLeft: "12px",
                fontSize: "14px",
                verticalAlign: "middle",
              }}
            >
              {t("archive.archived")}
            </span>
          )}
        </h1>
        <div className="header-actions">
          {/* ARCHIVE: restructured from the original 3-way ternary
              (edit+delete / save+cancel / delete-only-on-timeManagement)
              into edit-mode vs. not, with archive state handled inside
              the "not editing" branch. Functionally equivalent for the
              non-archived case, plus archive/reactivate/permanent-delete. */}
          {currentEditMode ? (
            <>
              <button
                onClick={() => {
                  switch (activeTab) {
                    case "info":
                      handleSaveInfo();
                      break;
                    case "departments":
                      handleSaveDepartments();
                      break;
                    case "exceptions":
                      setEditModeExceptions(false);
                      break;
                  }
                }}
                className="save-button"
                disabled={loading}
              >
                {loading ? t("buttons.saving") : t("buttons.save")}
              </button>
              <button
                onClick={handleCancel}
                className="cancel-button-professional"
              >
                {t("buttons.cancel")}
              </button>
            </>
          ) : (
            <>
              {activeTab !== "timeManagement" && !isArchived && (
                <button onClick={handleEditClick} className="edit-button">
                  {t("buttons.edit")}
                </button>
              )}
              {isArchived ? (
                <>
                  <button
                    onClick={handleReactivateStaff}
                    className="edit-button"
                  >
                    {t("archive.reactivate")}
                  </button>
                  <button
                    onClick={handleDeleteStaff}
                    className="delete-button"
                  >
                    {t("archive.deletePermanently")}
                  </button>
                </>
              ) : (
                <button onClick={handleArchiveStaff} className="delete-button">
                  {t("archive.archive")}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* ------------------------------------------------------------------ */}
      {/* Tabs                                                                */}
      {/* ------------------------------------------------------------------ */}

      <div className="tabs">
        <button
          className={`tab ${activeTab === "info" ? "active" : ""}`}
          onClick={() => setActiveTab("info")}
        >
          {t("detail.tabs.personalInfo")}
        </button>
        <button
          className={`tab ${activeTab === "departments" ? "active" : ""}`}
          onClick={() => setActiveTab("departments")}
        >
          {t("detail.tabs.departments")}
        </button>
        <button
          className={`tab ${activeTab === "exceptions" ? "active" : ""}`}
          onClick={() => setActiveTab("exceptions")}
        >
          {t("detail.tabs.scheduleExceptions")}
        </button>
        <button
          className={`tab ${activeTab === "timeManagement" ? "active" : ""}`}
          onClick={() => setActiveTab("timeManagement")}
        >
          {t("detail.tabs.timeManagement")}
        </button>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Tab content                                                         */}
      {/* ------------------------------------------------------------------ */}

      <div className="tab-content">

        {/* ----------------------------------------------------------------
            Info tab
        ---------------------------------------------------------------- */}
        {activeTab === "info" && (
          <div className="info-tab">
            {editModeInfo ? (
              <div className="edit-form">
                <div className="form-group">
                  <label htmlFor="name">{t("detail.personalInfo.name")}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formDataInfo.name}
                    onChange={handleInfoInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">
                    {t("detail.personalInfo.email")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formDataInfo.email}
                    onChange={handleInfoInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="pin">{t("detail.personalInfo.pin")}</label>
                  <input
                    type="password"
                    id="pin"
                    name="pin"
                    value={formDataInfo.pin}
                    onChange={handleInfoInputChange}
                    pattern="\d{4}"
                    title={t("detail.personalInfo.pinPattern")}
                    placeholder={t("detail.personalInfo.pinPlaceholder")}
                    maxLength="4"
                  />
                  <small>{t("detail.personalInfo.pinHelp")}</small>
                </div>
                <div className="form-group">
                  <label htmlFor="role">{t("detail.personalInfo.role")}</label>
                  <select
                    id="role"
                    name="role"
                    value={formDataInfo.role}
                    onChange={handleInfoInputChange}
                  >
                    <option value="staff">{t("roles.staff")}</option>
                    <option value="manager">{t("roles.manager")}</option>
                    <option value="admin">{t("roles.admin")}</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="info-display">
                <div className="info-field">
                  <span className="field-label">
                    {t("detail.personalInfo.name")}:
                  </span>
                  <span className="field-value">{staff.name}</span>
                </div>
                <div className="info-field">
                  <span className="field-label">
                    {t("detail.personalInfo.email")}:
                  </span>
                  <span className="field-value">
                    {staff.email || t("detail.personalInfo.notSet")}
                  </span>
                </div>
                <div className="info-field">
                  <span className="field-label">
                    {t("detail.personalInfo.pin")}:
                  </span>
                  <span className="field-value">
                    {hasPinSet(staff)
                      ? t("detail.personalInfo.pinMask")
                      : t("detail.personalInfo.notSet")}
                  </span>
                </div>
                <div className="info-field">
                  <span className="field-label">
                    {t("detail.personalInfo.role")}:
                  </span>
                  <span className="field-value">
                    {t(`roles.${staff.role}`)}
                  </span>
                </div>
                <div className="info-field">
                  <span className="field-label">
                    {t("detail.personalInfo.startDate")}:
                  </span>
                  <span className="field-value">
                    {staff.employmentInfo?.startDate?.toLocaleDateString(
                      "de-DE",
                      {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      },
                    ) || t("detail.personalInfo.notSet")}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ----------------------------------------------------------------
            Departments tab
        ---------------------------------------------------------------- */}
        {activeTab === "departments" && (
          <div className="departments-tab">
            <h3>{t("detail.departments.title")}</h3>

            {departments.length === 0 ? (
              <div className="no-departments-message">
                <p>{t("detail.departments.noAvailable")}</p>
              </div>
            ) : editModeDepartments ? (
              <div className="departments-edit">
                <div className="form-group">
                  <label htmlFor="primaryDepartmentId">
                    {t("detail.departments.primary")}
                  </label>
                  <select
                    id="primaryDepartmentId"
                    name="primaryDepartmentId"
                    value={formDataDepartments.primaryDepartmentId}
                    onChange={handlePrimaryDepartmentChange}
                  >
                    <option value="">
                      {t("detail.departments.selectPrimary")}
                    </option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="departmentIds">
                    {t("detail.departments.assigned")}
                  </label>
                  <select
                    id="departmentIds"
                    name="departmentIds"
                    multiple
                    value={formDataDepartments.departmentIds}
                    onChange={handleDepartmentChange}
                    className="multiselect"
                  >
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  <small>{t("detail.departments.multiSelectHelp")}</small>
                </div>
              </div>
            ) : (
              <div className="departments-display">
                <div className="department-list">
                  <div className="department-field">
                    <span className="field-label">
                      {t("detail.departments.primary")}:
                    </span>
                    <span className="field-value">
                      {staff.primaryDepartmentId
                        ? getDepartmentName(staff.primaryDepartmentId)
                        : t("detail.departments.none")}
                    </span>
                  </div>
                  <div className="department-field">
                    <span className="field-label">
                      {t("detail.departments.assigned")}:
                    </span>
                    <ul className="department-list">
                      {staff.departmentIds && staff.departmentIds.length > 0 ? (
                        staff.departmentIds.map((deptId) => (
                          <li key={deptId} className="department-item">
                            {getDepartmentName(deptId)}
                            {deptId === staff.primaryDepartmentId && (
                              <span className="primary-badge">
                                {t("detail.departments.primaryBadge")}
                              </span>
                            )}
                          </li>
                        ))
                      ) : (
                        <li>{t("detail.departments.noAssigned")}</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ----------------------------------------------------------------
            Exceptions tab (Time Off and Exceptions)
        ---------------------------------------------------------------- */}
        {activeTab === "exceptions" && (
          <div className="exceptions-tab">
            <h3>{t("detail.exceptions.title")}</h3>

            {/* Vacation Account Card */}
            <div className="vacation-account-section">
              <VacationAccountCard
                staffId={staffId}
                companyId={companyId}
                onManageClick={handleOpenVacationModal}
              />
            </div>

            {/* Hours Account Card */}
            <div className="hours-account-section">
              <HoursAccountCard
                staffId={staffId}
                companyId={companyId}
                onManageClick={handleOpenHoursModal}
                onAccountLoaded={handleHoursAccountLoaded}
                refreshTrigger={hoursRefreshTrigger}
              />
            </div>

            {editModeExceptions && (
              <div className="add-exception-form">
                <h4>{t("detail.exceptions.addTitle")}</h4>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    handleAddException({
                      date: formData.get("date"),
                      type: formData.get("type"),
                      hours: formData.get("hours"),
                      description: formData.get("description"),
                    });
                    e.target.reset();
                  }}
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="date">
                        {t("detail.exceptions.date")}
                      </label>
                      <input type="date" id="date" name="date" required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="type">
                        {t("detail.exceptions.type")}
                      </label>
                      <select id="type" name="type" required>
                        <option value="vacation">
                          {t("detail.exceptions.types.vacation")}
                        </option>
                        <option value="sick">
                          {t("detail.exceptions.types.sick")}
                        </option>
                        <option value="off">
                          {t("detail.exceptions.types.off")}
                        </option>
                        <option value="custom">
                          {t("detail.exceptions.types.custom")}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="hours">
                        {t("detail.exceptions.hours")}
                      </label>
                      <input
                        type="number"
                        id="hours"
                        name="hours"
                        min="0"
                        step="0.5"
                        defaultValue="0"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="description">
                        {t("detail.exceptions.description")}
                      </label>
                      <input type="text" id="description" name="description" />
                    </div>
                  </div>
                  <button type="submit" className="add-button">
                    {t("detail.exceptions.addButton")}
                  </button>
                </form>
              </div>
            )}

            {/*
              Planned vacation days fetched from the vacations collection.
              These are vacation bookings made via the Team Vacation Calendar
              and are stored separately from schedule exceptions.
            */}
            <div className="planned-vacations-section">
              <div className="planned-vacations-header">
                <h4 className="planned-vacations-title">
                  {t("detail.exceptions.existing")}
                </h4>
                {!vacationsLoading && (
                  <span className="vacation-count-badge">
                    {staffVacations.length}
                  </span>
                )}
              </div>

              {vacationsLoading ? (
                <div className="planned-vacations-loading">
                  <div className="pv-spinner" />
                  <p>{t("page.loading")}</p>
                </div>
              ) : staffVacations.length > 0 ? (
                <div className="planned-vacation-list">
                  {[...staffVacations]
                    .sort((a, b) =>
                      getVacationDateString(a).localeCompare(
                        getVacationDateString(b),
                      ),
                    )
                    .map((vacation) => {
                      const dateStr = getVacationDateString(vacation);
                      const date = new Date(dateStr + "T00:00:00");
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isPast = date < today;

                      return (
                        <div
                          key={vacation.id}
                          className={`planned-vacation-item ${isPast ? "is-past" : "is-upcoming"}`}
                        >
                          <div className="pv-date-badge">
                            <span className="pv-month">
                              {date.toLocaleDateString("de-DE", {
                                month: "short",
                              })}
                            </span>
                            <span className="pv-day">{date.getDate()}</span>
                          </div>
                          <div className="pv-content">
                            <span className="pv-full-date">
                              {date.toLocaleDateString("de-DE", {
                                weekday: "long",
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })}
                            </span>
                            {vacation.departmentName && (
                              <span className="pv-department-tag">
                                {vacation.departmentName}
                              </span>
                            )}
                          </div>
                          <span
                            className={`pv-status-dot ${isPast ? "is-past" : "is-upcoming"}`}
                          />
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="planned-vacations-empty">
                  <p>{t("detail.exceptions.noExceptions")}</p>
                </div>
              )}
            </div>

            {/* Vacation Account Modal */}
            {isVacationModalOpen && (
              <VacationAccountModal
                isOpen={isVacationModalOpen}
                onClose={handleCloseVacationModal}
                staffId={staffId}
                companyId={companyId}
                staffName={staff?.name}
                year={selectedYear}
              />
            )}

            {/* Hours Account Modal */}
            {isHoursModalOpen && (
              <HoursAccountModal
                isOpen={isHoursModalOpen}
                onClose={handleCloseHoursModal}
                staffId={staffId}
                companyId={companyId}
                staffName={staff?.name}
                year={selectedHoursYear}
                hoursAccount={hoursAccount}
                onAccountChanged={handleHoursAccountChanged}
              />
            )}
          </div>
        )}

        {/* ----------------------------------------------------------------
            Time management tab
        ---------------------------------------------------------------- */}
        {activeTab === "timeManagement" && (
          <TimeManagementTab
            staffId={staffId}
            companyId={companyId}
            staff={staff}
          />
        )}

      </div>
    </div>
  );
};

export default StaffDetailPage;